import { X } from 'lucide-react';
import { Button } from '@/components/Button';
import type { JobWithRelations } from '@maker-toolchest/shared';

interface CancelJobDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  job: JobWithRelations;
  loading?: boolean;
}

export function CancelJobDialog({
  open,
  onClose,
  onConfirm,
  job,
  loading = false,
}: CancelJobDialogProps) {
  if (!open) return null;

  const incompleteTasks = job.tasks.filter(
    (task) => task.status !== 'COMPLETED' && task.status !== 'CANCELLED'
  ).length;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-background rounded-lg border border-border shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">Cancel Job</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-md p-2 text-foreground-muted hover:bg-background-hover hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-foreground mb-4">
            The <span className="font-semibold">{job.name}</span> job has{' '}
            <span className="font-semibold">{incompleteTasks}</span>{' '}
            {incompleteTasks === 1 ? 'incomplete task' : 'incomplete tasks'}.
          </p>
          <p className="text-sm text-foreground-muted">
            Are you sure you want to cancel this job? This action cannot be undone.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            No, Keep Job
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          >
            Yes, Cancel Job
          </Button>
        </div>
      </div>
    </>
  );
}
